<template>
  <div
    class="fixed inset-0 backdrop-blur-[2px] bg-[rgba(20,17,11,0.8)] lg:hidden z-50 transition-opacity duration-300"
    aria-hidden="true"
    :class="isMobileFilterSheetOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'"
    @click="isMobileFilterSheetOpen = !isMobileFilterSheetOpen"
  />
  <!-- Mobile Filter Sheet -->
  <div
    class="fixed bottom-0 left-0 z-50 w-screen h-auto max-h-screen bg-bg-canvas flex-col transition-all duration-500 rounded-t-2xl"
    :class="
      isMobileFilterSheetOpen
        ? 'opacity-100 translate-y-0'
        : 'opacity-0 pointer-events-none translate-y-full'
    "
  >
    <div class="w-full flex flex-row justify-between items-center p-4">
      <div class="flex flex-col semibold">
        <div class="text-sm text-strong flex flex-row gap-2">
          <Icon name="Search" class="w-4 h-4" />
          <span>Filters for Investments</span>
        </div>
        <div class="text-xl text-strongest">Select a Filter</div>
      </div>
      <Button @click="toggleMobileFilterSheet" variant="paper-primary" size="sm">
        <template #icon> <Icon name="X" class="h-4 w-4 fill-outline-darker" /></template>
      </Button>
    </div>
    <div class="w-full border-b border-outline-dividers"></div>
    <div class="flex flex-col gap-2 p-4 h-full overflow-scroll max-h-[60vh]">
      <!-- List of filters for mobile -->
      <div
        v-if="mobileSelectedFilter == null"
        class="flex flex-col gap-2 max-h-[60vh] overflow-auto"
      >
        <div
          v-for="filter in filters"
          class="flex gap-5 items-center w-full px-3 py-1.5"
          @click="mobileSelectFilter(filter)"
        >
          <Icon name="Filter" class="h-4 w-4 text-tab-icon-unselected" :stroke-width="3" />
          <p class="font-inter text-base text-strongest grow">{{ filter.label }}</p>
          <Icon name="ChevronRight" class="h-4 w-4 text-filter-outline" :stroke-width="3" />
        </div>
      </div>
      <!-- filter options -->
      <div v-else class="flex flex-col gap-2">
        <div class="flex items-center">
          <Button
            variant="paper-primary"
            size="sm"
            class="mr-3"
            type="button"
            @click="mobileSelectFilter(null)"
          >
            <template #icon>
              <Icon name="ChevronLeft" class="h-4 w-4" />
            </template>
          </Button>
          <!-- Render filter options for the selected filter -->
          <p class="font-inter text-base text-strongest grow">{{ mobileSelectedFilter.label }}</p>
        </div>

        <!-- This is a placeholder. You would implement the actual filter options here based on the filter type -->
        <div class="bg-canvas rounded-lg p-4 flex flex-col gap-4 h-full">
          <!-- Min/Max filter -->
          <div v-if="mobileSelectedFilter.filter_type === 'min-max'" class="flex flex-col gap-3">
            <BaseInput
              field_type="number"
              :field_name="`${mobileSelectedFilter.field_name}_min`"
              label="Min value"
              :place_holder="`Min ${mobileSelectedFilter.label}`"
              variant="secondary"
              v-model="pendingFilters[mobileSelectedFilter.field_name].min"
            />
            <BaseInput
              field_type="number"
              :field_name="`${mobileSelectedFilter.field_name}_max`"
              label="Max value"
              :place_holder="`Max ${mobileSelectedFilter.label}`"
              variant="secondary"
              v-model="pendingFilters[mobileSelectedFilter.field_name].max"
            />
          </div>

          <!-- Date Range filter -->
          <div v-if="mobileSelectedFilter.filter_type === 'date-range'" class="flex flex-col gap-3">
            <BaseInput
              field_type="date"
              :field_name="`${mobileSelectedFilter.field_name}_start`"
              label="Start Date"
              variant="secondary"
              v-model="pendingFilters[mobileSelectedFilter.field_name].start"
            />
            <BaseInput
              field_type="date"
              :field_name="`${mobileSelectedFilter.field_name}_end`"
              label="End Date"
              variant="secondary"
              v-model="pendingFilters[mobileSelectedFilter.field_name].end"
            />
          </div>

          <!-- Picklist filter (multi-select) -->
          <div v-if="mobileSelectedFilter.filter_type === 'picklist'" class="flex flex-col gap-3">
            <div
              v-for="option in mobileSelectedFilter.options"
              :key="option.value"
              @click="togglePicklistOption(mobileSelectedFilter, option)"
              class="cursor-pointer bg-container-bg text-strongest rounded-lg px-2 py-3 mb-1"
              :class="
                isPendingSelected(mobileSelectedFilter, option) ? 'bg-radio-checkbox-selector' : ''
              "
            >
              <div class="flex items-center justify-between">
                <div class="flex items-center gap-3">
                  <div
                    class="rounded-full border w-4 h-4 flex items-center justify-center"
                    :class="
                      isPendingSelected(mobileSelectedFilter, option)
                        ? 'border-radio-checkbox-checked bg-radio-checkbox-checked'
                        : 'border-radio-checkbox-outline'
                    "
                  >
                    <Icon
                      v-if="isPendingSelected(mobileSelectedFilter, option)"
                      name="Check"
                      class="h-3 w-3 text-white"
                      :stroke-width="3"
                    />
                  </div>
                  <p class="font-inter text-sm font-medium">{{ option.label }}</p>
                  <p v-if="option.description" class="font-inter text-light text-xs mt-0.5">
                    {{ option.description }}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <Button
            v-if="mobileSelectedFilter.filter_type !== 'picklist'"
            @click="
              applyFilter(mobileSelectedFilter, () => {
                mobileSelectFilter(null);
                isMobileFilterSheetOpen = false;
              })
            "
            size="md"
            variant="paper-primary"
            class="mt-1 justify-self-end"
          >
            <template #label>
              <p class="mt-0.5 text-xl">Apply filter</p>
            </template>
          </Button>
        </div>
      </div>
    </div>
  </div>
  <!-- Desktop Filter dropdowns -->
  <div class="flex flex-col gap-3 w-full">
    <div class="flex items-start justify-between w-full gap-3">
      <div class="hidden md:flex md:flex-col gap-2 flex-1">
        <div
          v-for="(filterRow, filterRowIndex) in filterRows"
          :key="filterRowIndex"
          class="items-center gap-2 flex flex-wrap"
        >
          <FilterDropdown
            v-for="filter in filterRow"
            :key="filter.field_name"
            :is-open="activeFilter === filter.field_name"
            @toggle="toggleFilterOpen(filter.field_name)"
            @close="closeFilter"
            dropdown-align="left"
          >
          <template #trigger="{ toggle }">
            <div
              @click="toggle"
              class="h-9 rounded-[100px] border border-filter-outline px-3 flex items-center cursor-pointer shrink-0 whitespace-nowrap"
              :class="
                activeFilters.some((f) => f.field_name === filter.field_name) ? 'bg-filter-bg' : ''
              "
            >
              <div class="flex items-center gap-2">
                <div
                  v-if="activeFilters.filter((f) => f.field_name === filter.field_name).length > 0"
                  class="rounded-full flex items-center justify-center font-inter bg-filter-bg-tag w-[21px] h-[20px] text-grey-1 text-sm"
                >
                  {{ activeFilters.filter((f) => f.field_name === filter.field_name).length }}
                </div>
                <p class="font-inter text-sm font-medium pr-3 text-strongest">{{ filter.label }}</p>
              </div>

              <Icon name="ChevronDown" class="h-4 w-4 text-filter-outline" :stroke-width="3" />
            </div>
          </template>

          <template #default="{ close }">
            <!-- Min/Max filter -->
            <div v-if="filter.filter_type === 'min-max'" class="flex flex-col gap-3">
              <BaseInput
                field_type="number"
                :field_name="`${filter.field_name}_min`"
                label="Min value"
                :place_holder="`Min ${filter.label}`"
                variant="secondary"
                v-model="pendingFilters[filter.field_name].min"
              />
              <BaseInput
                field_type="number"
                :field_name="`${filter.field_name}_max`"
                label="Max value"
                :place_holder="`Max ${filter.label}`"
                variant="secondary"
                v-model="pendingFilters[filter.field_name].max"
              />
            </div>

            <!-- Date Range filter -->
            <div v-if="filter.filter_type === 'date-range'" class="flex flex-col gap-3">
              <BaseInput
                field_type="date"
                :field_name="`${filter.field_name}_start`"
                label="Start Date"
                variant="secondary"
                v-model="pendingFilters[filter.field_name].start"
              />
              <BaseInput
                field_type="date"
                :field_name="`${filter.field_name}_end`"
                label="End Date"
                variant="secondary"
                v-model="pendingFilters[filter.field_name].end"
              />
            </div>

            <!-- Picklist filter (multi-select) -->
            <div v-if="filter.filter_type === 'picklist'" class="flex flex-col gap-3 mb-1">
              <div
                v-for="option in filter.options"
                :key="option.value"
                @click="togglePicklistOption(filter, option)"
                class="cursor-pointer bg-container-bg text-strongest rounded-lg px-2 py-3 mb-0"
                :class="isPendingSelected(filter, option) ? 'bg-radio-checkbox-selector' : ''"
              >
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-3">
                    <div
                      class="rounded-full border w-4 h-4 flex items-center justify-center"
                      :class="
                        isPendingSelected(filter, option)
                          ? 'border-radio-checkbox-checked bg-radio-checkbox-checked'
                          : 'border-radio-checkbox-outline'
                      "
                    >
                      <Icon
                        v-if="isPendingSelected(filter, option)"
                        name="Check"
                        class="h-3 w-3 text-white"
                        :stroke-width="3"
                      />
                    </div>
                    <p class="font-inter text-sm font-medium">{{ option.label }}</p>
                    <p v-if="option.description" class="font-inter text-light text-xs mt-0.5">
                      {{ option.description }}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <Button
              v-if="filter.filter_type !== 'picklist'"
              @click="applyFilter(filter, close)"
              variant="paper-primary"
              class="mt-1"
            >
              <template #label>
                <p class="mt-0.5 text-xl">Apply filter</p>
              </template>
            </Button>
          </template>
          </FilterDropdown>
        </div>
      </div>
      <div
        class="items-center gap-3 flex md:hidden rounded-full border border-filter-outline px-3 py-2 curson-pointer"
        @click="toggleMobileFilterSheet"
      >
        <p class="font-inter text-sm font-medium text-strongest">Filters</p>
        <Icon name="ChevronDown" class="h-4 w-4 text-filter-outline" :stroke-width="3" />
      </div>

      <Button
        @click="emit('clear-all-filters')"
        size="sm"
        variant="paper-primary"
        class="shrink-0"
      >
        <template #label>Clear All</template>
      </Button>
    </div>

    <!-- Active filter chips -->
    <div v-if="!hidePills && activeFilters.length > 0" class="flex items-center gap-2 flex-wrap">
      <div
        v-for="(activeFilter, index) in activeFilters"
        :key="`${activeFilter.field_name}-${activeFilter.value}-${index}`"
        class="flex items-center justify-between bg-filter-bg-selected-chip rounded-lg py-1 pl-2 pr-1.5 animate-slideup"
      >
        <p class="font-inter text-sm font-medium text-strongest pr-4">
          {{ activeFilter.label }}: {{ activeFilter.display_value }}
        </p>
        <Icon
          @click="emit('clear-filter', activeFilter)"
          name="X"
          class="h-5 w-5 text-filter-outline-selected-chip-icon cursor-pointer"
          :stroke-width="2.5"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, watch, computed } from "vue";
import { Button, Icon, FilterDropdown, BaseInput } from "@/components";

const emit = defineEmits(["clear-filter", "clear-all-filters", "apply-filters"]);

const activeFilter = ref(null);

const props = defineProps({
  filters: {
    type: Array,
    required: true,
  },
  activeFilters: {
    type: Array,
    required: true,
  },
  hidePills: {
    type: Boolean,
    default: false,
  },
});

// State for mobile filter sheet
const isMobileFilterSheetOpen = ref(false);
const mobileSelectedFilter = ref(null); // which filter is selected in mobile sheet for showing options
const test = "";

// Desktop pills wrap at a fixed count per row rather than the flex-wrap default,
// so a resized container can't reshuffle which filters share a row.
const FILTERS_PER_ROW = 5;
const filterRows = computed(() => {
  const rows = [];
  for (let i = 0; i < props.filters.length; i += FILTERS_PER_ROW) {
    rows.push(props.filters.slice(i, i + FILTERS_PER_ROW));
  }
  return rows;
});

// Pending filters state - tracks selections before "Apply" is clicked
const pendingFilters = reactive({});

// Initialize pending state structure when filters change
watch(
  () => props.filters,
  (filters) => {
    filters.forEach((filter) => {
      if (!pendingFilters[filter.field_name]) {
        if (filter.filter_type === "min-max") {
          pendingFilters[filter.field_name] = { min: null, max: null };
        } else if (filter.filter_type === "date-range") {
          pendingFilters[filter.field_name] = { start: null, end: null };
        } else if (filter.filter_type === "picklist") {
          pendingFilters[filter.field_name] = { selected: [] };
        }
      }
    });
  },
  { immediate: true }
);

function toggleFilterOpen(dropdownName) {
  if (activeFilter.value === dropdownName) {
    activeFilter.value = null;
  } else {
    // Initialize pending state from active filters when opening
    const filter = props.filters.find((f) => f.field_name === dropdownName);
    if (filter) {
      initializePendingFromActive(filter);
    }
    activeFilter.value = dropdownName;
  }
}

function initializePendingFromActive(filter) {
  const existingFilter = props.activeFilters.find((f) => f.field_name === filter.field_name);

  if (filter.filter_type === "picklist") {
    const existingFilters = props.activeFilters.filter((f) => f.field_name === filter.field_name);
    pendingFilters[filter.field_name].selected = existingFilters.map((f) => f.value);
  } else if (filter.filter_type === "min-max") {
    pendingFilters[filter.field_name].min = existingFilter?.min ?? null;
    pendingFilters[filter.field_name].max = existingFilter?.max ?? null;
  } else if (filter.filter_type === "date-range") {
    pendingFilters[filter.field_name].start = existingFilter?.start ?? null;
    pendingFilters[filter.field_name].end = existingFilter?.end ?? null;
  }
}

function closeFilter() {
  activeFilter.value = null;
}

function buildPicklistFilters(filter) {
  return pendingFilters[filter.field_name].selected.map((value) => {
    const option = filter.options.find((o) => o.value === value);
    return {
      label: filter.label,
      field_name: filter.field_name,
      value: value,
      display_value: option?.label || value,
      filter_type: filter.filter_type,
      table: filter.table,
    };
  });
}

// Picklist options are a single click with no manual input, so they apply
// immediately instead of waiting on the "Apply filter" CTA (min-max/date-range
// keep that CTA since there's text/date entry to commit).
function togglePicklistOption(filter, option) {
  const selected = pendingFilters[filter.field_name].selected;
  const index = selected.indexOf(option.value);
  if (index > -1) {
    selected.splice(index, 1);
  } else {
    selected.push(option.value);
  }
  emit("apply-filters", { field_name: filter.field_name, filters: buildPicklistFilters(filter) });
}

function isPendingSelected(filter, option) {
  return pendingFilters[filter.field_name]?.selected?.includes(option.value);
}

function formatCurrency(value) {
  if (value === null || value === "") return null;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function formatNumber(value) {
  if (value === null || value === "") return null;
  return new Intl.NumberFormat("en-US").format(value);
}

function formatDate(value) {
  if (!value) return null;
  const date = new Date(value);
  return date.toLocaleDateString("en-US", {
    month: "numeric",
    day: "numeric",
    year: "numeric",
  });
}

function applyFilter(filter, closeFn) {
  const newFilters = [];
  const pending = pendingFilters[filter.field_name];

  if (filter.filter_type === "picklist") {
    newFilters.push(...buildPicklistFilters(filter));
  } else if (filter.filter_type === "min-max") {
    const hasMin = pending.min !== null && pending.min !== "";
    const hasMax = pending.max !== null && pending.max !== "";

    if (hasMin || hasMax) {
      const formatValue =
        filter.number_type === "currency" ? formatCurrency : formatNumber;
      const minFormatted = hasMin ? formatValue(pending.min) : null;
      const maxFormatted = hasMax ? formatValue(pending.max) : null;

      let displayValue;
      if (hasMin && hasMax) {
        displayValue = `${minFormatted} - ${maxFormatted}`;
      } else if (hasMin) {
        displayValue = `${minFormatted}+`;
      } else {
        displayValue = `Up to ${maxFormatted}`;
      }

      newFilters.push({
        label: filter.label,
        field_name: filter.field_name,
        min: hasMin ? pending.min : null,
        max: hasMax ? pending.max : null,
        display_value: displayValue,
        filter_type: filter.filter_type,
        table: filter.table,
      });
    }
  } else if (filter.filter_type === "date-range") {
    const hasStart = pending.start !== null && pending.start !== "";
    const hasEnd = pending.end !== null && pending.end !== "";

    if (hasStart || hasEnd) {
      const startFormatted = hasStart ? formatDate(pending.start) : null;
      const endFormatted = hasEnd ? formatDate(pending.end) : null;

      let displayValue;
      if (hasStart && hasEnd) {
        displayValue = `${startFormatted} - ${endFormatted}`;
      } else if (hasStart) {
        displayValue = `From ${startFormatted}`;
      } else {
        displayValue = `Until ${endFormatted}`;
      }

      newFilters.push({
        label: filter.label,
        field_name: filter.field_name,
        start: hasStart ? pending.start : null,
        end: hasEnd ? pending.end : null,
        display_value: displayValue,
        filter_type: filter.filter_type,
        table: filter.table,
      });
    }
  }

  emit("apply-filters", { field_name: filter.field_name, filters: newFilters });
  closeFn();
}

function toggleMobileFilterSheet() {
  isMobileFilterSheetOpen.value = !isMobileFilterSheetOpen.value;
  if (!isMobileFilterSheetOpen.value) {
    mobileSelectedFilter.value = null; // reset to filter list view whenever opening the sheet
  }
}

function mobileSelectFilter(filter) {
  mobileSelectedFilter.value = filter || null;
  toggleFilterOpen(filter ? filter.field_name : null);
}
</script>
